import { Subscription } from '@innovyze/shared-utils';
import { TFunc } from '@Translations';
import { InnovyzeRole } from '@innovyze/stylovyze';

export enum SubscriptionCardColor {
	INSIGHT = '#89216B',
	WATER = '#007CA0',
	ASSET = '#EFB304',
	ICM = '#674EA7',
	PLANT = '#34C0AC',
}

export const INNOVYZE_ROLE = (t: TFunc) => {
	return {
		[InnovyzeRole.SUPER_ADMIN]: t('SuperAdmin'),
		[InnovyzeRole.ADMIN]: t('Admin'),
		[InnovyzeRole.READ_WRITE]: t('Editor'),
		[InnovyzeRole.READ_ONLY]: t('User'),
	};
};

export const RoleList = (
	t: TFunc,
): {
	key: InnovyzeRole;
	value: string;
}[] => [
	{
		key: InnovyzeRole.SUPER_ADMIN,
		value: INNOVYZE_ROLE(t)[InnovyzeRole.SUPER_ADMIN],
	},
	{
		key: InnovyzeRole.ADMIN,
		value: INNOVYZE_ROLE(t)[InnovyzeRole.ADMIN],
	},
	{
		key: InnovyzeRole.READ_WRITE,
		value: INNOVYZE_ROLE(t)[InnovyzeRole.READ_WRITE],
	},
	{
		key: InnovyzeRole.READ_ONLY,
		value: INNOVYZE_ROLE(t)[InnovyzeRole.READ_ONLY],
	},
];

export const TRANSLATED_SUBSCRIPTION_NAMES = (t: TFunc) => {
	return {
		[Subscription.INSIGHT_T1]: t('Insight Tier 1'),
		[Subscription.INSIGHT_T2]: t('Insight Tier 2'),
		[Subscription.INSIGHT_T3]: t('Insight Tier 3'),
		[Subscription.INSIGHT_CM]: t('Insight Custom'),
		[Subscription.ASSET_FULL_ACCESS]: t('Asset Full Access'),
		[Subscription.ASSET_VIEWER_ONLY_ACCESS]: t('Asset Viewer Only Access'),
		[Subscription.ASSET_INSPECTION_FIELD]: t('Asset Inspection Field'),
		[Subscription.WATER_T1]: t('Water Tier 1'),
		[Subscription.WATER_T2]: t('Water Tier 2'),
		[Subscription.WATER_T3]: t('Water Tier 3'),
		[Subscription.ICM_1D]: t('ICM 1D'),
		[Subscription.ICM_1D_2D]: t('ICM 1D 2D'),
		[Subscription.PLANT_CM]: t('Plant Custom'),
		[Subscription.PLANT_T1]: t('Plant Tier 1'),
		[Subscription.PLANT_T2]: t('Plant Tier 2'),
		[Subscription.PLANT_T3]: t('Plant Tier 3'),
		[Subscription.PLANT_T4]: t('Plant Tier 4'),
		[Subscription.INFO360DF]: t('Info360 Demand Forecast'),
	} as { [key in Subscription]: string };
};

export const subscriptionsList = (
	t: TFunc,
): {
	key: Subscription;
	value: string;
	color: string;
}[] => [
	{
		key: Subscription.INSIGHT_T1,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.INSIGHT_T1],
		color: SubscriptionCardColor.INSIGHT,
	},
	{
		key: Subscription.INSIGHT_T2,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.INSIGHT_T2],
		color: SubscriptionCardColor.INSIGHT,
	},
	{
		key: Subscription.INSIGHT_T3,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.INSIGHT_T3],
		color: SubscriptionCardColor.INSIGHT,
	},
	{
		key: Subscription.INSIGHT_CM,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.INSIGHT_CM],
		color: SubscriptionCardColor.INSIGHT,
	},
	{
		key: Subscription.ASSET_FULL_ACCESS,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.ASSET_FULL_ACCESS],
		color: SubscriptionCardColor.ASSET,
	},
	{
		key: Subscription.ASSET_VIEWER_ONLY_ACCESS,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[
			Subscription.ASSET_VIEWER_ONLY_ACCESS
		],
		color: SubscriptionCardColor.ASSET,
	},
	{
		key: Subscription.ASSET_INSPECTION_FIELD,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[
			Subscription.ASSET_INSPECTION_FIELD
		],
		color: SubscriptionCardColor.ASSET,
	},
	{
		key: Subscription.WATER_T1,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.WATER_T1],
		color: SubscriptionCardColor.WATER,
	},
	{
		key: Subscription.WATER_T2,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.WATER_T2],
		color: SubscriptionCardColor.WATER,
	},
	{
		key: Subscription.WATER_T3,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.WATER_T3],
		color: SubscriptionCardColor.WATER,
	},
	{
		key: Subscription.ICM_1D,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.ICM_1D],
		color: SubscriptionCardColor.ICM,
	},
	{
		key: Subscription.ICM_1D_2D,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.ICM_1D_2D],
		color: SubscriptionCardColor.ICM,
	},
	{
		key: Subscription.PLANT_CM,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.PLANT_CM],
		color: SubscriptionCardColor.PLANT,
	},
	{
		key: Subscription.PLANT_T1,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.PLANT_T1],
		color: SubscriptionCardColor.PLANT,
	},
	{
		key: Subscription.PLANT_T2,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.PLANT_T2],
		color: SubscriptionCardColor.PLANT,
	},
	{
		key: Subscription.PLANT_T3,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.PLANT_T3],
		color: SubscriptionCardColor.PLANT,
	},
	{
		key: Subscription.PLANT_T4,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.PLANT_T4],
		color: SubscriptionCardColor.PLANT,
	},
	{
		key: Subscription.INFO360DF,
		value: TRANSLATED_SUBSCRIPTION_NAMES(t)[Subscription.INFO360DF],
		color: SubscriptionCardColor.PLANT,
	},
];

export const SubscriptionsValueKeyMap = (t: TFunc) =>
	new Map(Array.from(subscriptionsList(t), e => [e.value, e.key]));
